<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { debounce } from 'lodash-es'
import plural from 'plural-ru'
import dayjs from '@/lib/dayjs'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { apiPost, httpPost } from '@/lib/http'
import { mapFile } from '@/helpers/dataMappers'
import { currentUser } from '@/lib/currentUser'
import type { MarketNamedItem, MarketProductFileRaw, MarketProductUser } from '@/types/market'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import ProductReview from '@/ui-kit/products/ProductReview.vue'
import UserInline from '@/ui-kit/users/UserInline.vue'
import UserSearchBlock from '@/ui-kit/users/UserSearchBlock.vue'

interface ProductData {
  id: number
  name: string
  active: boolean
  content_types: MarketNamedItem[]
  coauthors: MarketProductUser[]
  creator: MarketProductUser
  review_count: number
  avg_rating: number
  image: string
  banned: boolean
  is_creator: boolean
  is_owned: boolean
  can_buy: boolean
  lock_reason: string
  price: number | null
  inspired_by_ace_state: 'n' | 'p' | 'y' | 'r'
  inspired_by_ace_reject_reason: string | null
  description: string
  files_preview: MarketProductFileRaw[]
  files: MarketProductFileRaw[]
  demo_files: MarketProductFileRaw[]
  has_my_review: boolean
  courses: MarketNamedItem[]
  holiday: MarketNamedItem | null
}

interface CurrentUser {
  id: number
  image: string
  name: string
}

interface PageContext {
  product: ProductData
  user: CurrentUser
  urls: {
    list_products: string
    edit_product: string
    purchase_bonus: string
    request_teachers_for_gift: string
    request_add_or_edit_review: string
    request_add_or_edit_response: string
    request_reviews: string
    request_purchase_product: string
  }
}

interface Breadcrumb {
  name: string
  url: string
}

interface ReviewApiItem {
  id: number
  creator_id: number
  creator_name: string
  creator_profile_image: string
  creator_link: string
  rating: number
  text: string
  creation_date: string | number
  modification_date: string | number
  response_text: string
}

interface AddOrEditReviewResponse {
  review_id?: number
  failed?: boolean
}

interface AddOrEditResponseResponse {
  success?: boolean
}

interface TeachersForGiftUser {
  id: number
  image: string
  name: string
}

interface PurchaseResponse {
  failed?: boolean
  success?: boolean
}

interface ProductReviewItem {
  id: number
  rating: number
  text: string
  responseText: string
  date: string
  modifiedAt: string
  isReviewer: boolean
  user: {
    name: string
    image: string
    link: string
  }
}

interface StubReview {
  id?: number | string
  user: CurrentUser
  [key: string]: unknown
}

const ctx = usePageContext<PageContext>()
const { notify } = useNotification()
const { isMobile } = useScreen()

const product = ref<ProductData>(ctx.product)

const breadcrumbs = ref<Breadcrumb[]>([
  ...product.value.content_types.map(content => ({
    name: content.name,
    url: `${ctx.urls.list_products}?content_types=${content.id}`
  }))
])

if (product.value.holiday) {
  breadcrumbs.value.push({
    name: product.value.holiday.name,
    url: `${ctx.urls.list_products}?holidays=${product.value.holiday.id}`
  })
}

breadcrumbs.value.push(
  ...product.value.courses.map(course => ({
    name: course.name,
    url: `${ctx.urls.list_products}?courses=${course.id}`
  }))
)

const buyModal = ref(false)
const isOrderToGift = ref(false)
const isGiftSuccessSended = ref(false)
const loadedReviewPages = ref(0)
const reviewsPerPage = ref(20)
const reviews = ref<ProductReviewItem[]>([])
const giftOwnerSearch = ref('')
const giftOwner = ref<TeachersForGiftUser | null>(null)
const usersToGift = ref<TeachersForGiftUser[]>([])
const stubReview = ref<StubReview>({ user: ctx.user })
const inspiredRejectReasonModal = ref(false)

const bonusPoints = computed(() => {
  const bonusCamel = currentUser.bonusPoints
  if (typeof bonusCamel === 'number') return bonusCamel
  return 0
})

const currentUserId = computed(() => {
  return typeof currentUser.id === 'number' ? currentUser.id : 0
})

const hasBonusesToBuy = computed(() => bonusPoints.value >= (product.value.price ?? 0))

const canLoadMore = computed(() => reviews.value.length < product.value.review_count)

const previewFiles = computed(() => product.value.files_preview.map(mapFile))
const productFiles = computed(() => product.value.files.map(mapFile))
const demoFiles = computed(() => product.value.demo_files.map(mapFile))

const currentPage = computed(() => {
  return encodeURIComponent(window.location.href.slice(window.location.origin.length))
})

function reviewsText(count: number): string {
  return `${count} ${plural(count, 'отзыв', 'отзыва', 'отзывов')}`
}

async function fetchUsers(search: string) {
  const result = await apiPost<TeachersForGiftUser[]>(ctx.urls.request_teachers_for_gift, {
    search_text: search,
    product_id: product.value.id
  })

  if (result.failed) return
  usersToGift.value = result.data
}

const fetchUsersDebounced = debounce((search: string) => {
  fetchUsers(search)
}, 300)

watch(giftOwnerSearch, search => {
  fetchUsersDebounced(search)
})

async function createReview(payload: { text: string; rating: number }) {
  try {
    const { review_id, failed } = await httpPost<AddOrEditReviewResponse>(ctx.urls.request_add_or_edit_review, {
      text: payload.text,
      rating: payload.rating,
      product_id: product.value.id
    })

    if (failed) {
      throw new Error('failed')
    }

    if (review_id) {
      product.value.has_my_review = true
      reviews.value.unshift({
        user: {
          name: stubReview.value.user.name,
          image: stubReview.value.user.image,
          link: ''
        },
        text: payload.text,
        rating: payload.rating,
        isReviewer: true,
        id: review_id,
        responseText: '',
        date: dayjs().format('DD.MM.YYYY'),
        modifiedAt: dayjs().format('DD.MM.YYYY')
      })

      notify({ type: 'success', title: 'Отзыв успешно сохранен.' })
    }
  } catch (error) {
    notify({
      type: 'error',
      title: 'Не удалось отправить отзыв'
    })
    console.error(error)
  }
}

async function sendReview(updatedReview: { text: string; rating: number }) {
  try {
    const { review_id, failed } = await httpPost<AddOrEditReviewResponse>(ctx.urls.request_add_or_edit_review, {
      ...updatedReview,
      product_id: product.value.id
    })

    if (failed) {
      throw new Error('failed')
    }

    if (review_id) {
      reviews.value = reviews.value.map(review => {
        if (review.id !== review_id) {
          return review
        }

        return {
          ...review,
          ...updatedReview
        }
      })

      notify({ type: 'success', title: 'Отзыв успешно сохранен.' })
    }
  } catch (error) {
    notify({
      type: 'error',
      title: 'Не удалось отправить отзыв'
    })
    console.error(error)
  }
}

async function sendAnswer(payload: { text: string; reviewId: number }) {
  try {
    const result = await httpPost<AddOrEditResponseResponse>(ctx.urls.request_add_or_edit_response, {
      review_id: payload.reviewId,
      response_text: payload.text
    })

    if (!result.success) {
      throw new Error('failed')
    }

    reviews.value = reviews.value.map(review => ({
      ...review,
      responseText: review.id === payload.reviewId ? payload.text : review.responseText
    }))
  } catch (error) {
    notify({
      type: 'error',
      title: 'Не удалось отправить ответ'
    })
    console.error(error)
  }
}

async function loadReviews() {
  const result = await apiPost<ReviewApiItem[]>(ctx.urls.request_reviews, {
    product_id: product.value.id,
    fr: loadedReviewPages.value * reviewsPerPage.value,
    to: (loadedReviewPages.value + 1) * reviewsPerPage.value
  })

  if (result.failed) return

  reviews.value.push(
    ...result.data.map(
      (review): ProductReviewItem => ({
        id: review.id,
        rating: review.rating,
        text: review.text,
        responseText: review.response_text,
        date: dayjs(review.creation_date).format('DD.MM.YYYY'),
        modifiedAt: dayjs(review.modification_date).format('DD.MM.YYYY'),
        isReviewer: review.creator_id === currentUserId.value,
        user: {
          name: review.creator_name,
          image: review.creator_profile_image,
          link: review.creator_link
        }
      })
    )
  )

  loadedReviewPages.value += 1
}

function openBuy() {
  isOrderToGift.value = false
  buyModal.value = true
}

function closeSuccessGift() {
  isGiftSuccessSended.value = false
  buyModal.value = false
}

async function buyProduct() {
  const query: Record<string, unknown> = {
    product_id: product.value.id
  }

  if (isOrderToGift.value) {
    if (giftOwner.value) {
      query.gift_for_user_id = giftOwner.value.id
    } else {
      notify({
        type: 'warn',
        title: 'Не выбран получатель подарка'
      })
      return
    }
  }

  const result = await httpPost<PurchaseResponse>(ctx.urls.request_purchase_product, query).catch(() => {
    notify({
      type: 'error',
      title: 'Не удалось купить товар'
    })
    return null
  })

  if (!result) {
    return
  }

  if (result.failed) {
    notify({
      type: 'error',
      title: 'Не удалось купить товар'
    })
    return
  }

  if (!isOrderToGift.value) {
    window.location.reload()
    return
  }

  isGiftSuccessSended.value = true
}

watch(buyModal, () => {
  isGiftSuccessSended.value = false
})

onMounted(() => {
  loadReviews()
})

onBeforeUnmount(() => {
  fetchUsersDebounced.cancel()
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="teacher" rules-url="/website/documents/oferta" />

    <div id="view-product" class="content">
      <v-notifications position="bottom right" />

      <header class="header">
        <div v-if="isMobile" class="header__breadcrumbs text-caption">
          <v-button
            v-for="breadcrumb in breadcrumbs"
            :key="breadcrumb.url"
            class="header__breadcrumb mr-1"
            :href="breadcrumb.url"
            mode="small flat primary"
          >
            {{ breadcrumb.name }}
          </v-button>
        </div>

        <div v-else class="header__breadcrumbs text-caption">
          <v-link
            v-for="breadcrumb in breadcrumbs"
            :key="breadcrumb.url"
            class="header__breadcrumb"
            :href="breadcrumb.url"
          >
            {{ breadcrumb.name }}
          </v-link>
        </div>

        <h1 class="header__title">
          <span v-if="!product.active" class="header__draft mr-half">Черновик</span>
          {{ product.name }}
        </h1>
      </header>

      <main class="product d-flex">
        <div class="product__info text-caption mb-2">
          <span class="product__type text-overline mr-3 mb-1">
            {{ product.content_types.map(content => content.name).join(', ') }}
          </span>

          <span class="product__creator flex-center mr-auto mr-d-2">
            {{ product.coauthors.length ? 'Авторы:' : 'Автор:' }}&nbsp;
            <UserInline :user="product.creator" />
            <span v-for="coauthor in product.coauthors" :key="coauthor.id" class="ml-1">
              <UserInline :user="coauthor" />
            </span>
          </span>

          <span class="product__reviews d-flex">
            {{ reviewsText(product.review_count) }}
            <v-rating class="product__rating ml-2" :value="product.avg_rating" />
          </span>
        </div>

        <div class="product__aside">
          <v-img
            class="product__image mb-1"
            resize
            :responsive="isMobile"
            :src="product.image"
            :alt="product.name"
            :width="304"
            :height="224"
          />

          <div class="product__actions">
            <div v-if="product.banned" class="product__banned mb-1">Товар заблокирован администрацией сайта</div>

            <div v-else-if="!product.active && product.is_creator" class="product__draft-card mb-1">
              Черновик не отображается на Ярмарке, но доступен купившим пользователям
            </div>

            <div v-if="product.is_owned" class="product__owned mb-1">
              <v-icon class="mr-1">mdi-check-bold</v-icon>
              Куплено
            </div>

            <template v-if="!product.is_owned && !product.is_creator">
              <v-button v-if="product.can_buy" mode="warn xlarge block" class="mb-1" @click="openBuy">
                <span class="flex-center text-h3">
                  Купить {{ product.price }}
                  <v-icon>hand</v-icon>
                </span>
              </v-button>

              <div v-else class="product__buy-warn pa-2 mb-1">
                {{ product.lock_reason }}
              </div>
            </template>

            <v-button
              v-if="product.is_creator"
              mode="outlined primary large block"
              class="product__edit mb-1"
              :href="`${ctx.urls.edit_product}/${product.id}`"
            >
              <v-icon class="mr-half">mdi-pencil</v-icon>
              Редактировать
            </v-button>

            <div v-else-if="product.can_buy" class="product__buy-actions">
              <v-button
                v-if="!product.is_owned"
                mode="outlined primary large block"
                class="product__balance-button mb-1"
                :href="`${ctx.urls.purchase_bonus}?next=${currentPage}`"
                target="_blank"
              >
                Пополнить баланс
              </v-button>
            </div>
          </div>
        </div>

        <div class="product__content mt-2 mt-d-6 ml-d-3">
          <div
            v-if="product.is_creator ? product.inspired_by_ace_state !== 'n' : product.inspired_by_ace_state === 'y'"
            class="product__inspired-badge"
            :class="{
              'product__inspired-badge_active': product.inspired_by_ace_state === 'y',
              'product__inspired-badge_rejected': product.inspired_by_ace_state === 'r'
            }"
          >
            <v-icon>mdi-crown</v-icon>

            <span v-if="product.inspired_by_ace_state === 'y'" class="text-overline"> Качество АС </span>
            <span v-else-if="product.inspired_by_ace_state === 'r'" class="text-overline"> Отклонено методистом </span>
            <span v-else class="text-overline">Ожидает проверки методиста</span>

            <v-menu-base v-if="product.inspired_by_ace_state !== 'y'" pos-horizontal="center">
              <template #activator="{ onclick }">
                <v-icon class="blue-text" size="20" @click="onclick">mdi-information-outline</v-icon>
              </template>

              <div class="pa-1" style="width: 300px; white-space: normal">
                <template v-if="product.inspired_by_ace_state === 'p'">
                  Товар ожидает проверки методистом.<br />
                  Материал уже доступен для покупки на Ярмарке мастеров.
                </template>

                <div v-if="product.inspired_by_ace_state === 'r'" class="d-flex flex-column">
                  <span>
                    Товар <b>не прошел</b> проверку методистом.<br />
                    Материал остается доступным на Ярмарке мастеров, но без знака качества.
                  </span>
                  <v-link mode="primary" class="text-body-bold mt-1" @click="inspiredRejectReasonModal = true">
                    Подробнее
                  </v-link>
                </div>
              </div>
            </v-menu-base>
          </div>

          <p class="product__description mb-3">{{ product.description }}</p>

          <template v-if="productFiles.length">
            <h3 class="d-flex text-caption">
              <span class="text-overline mr-3">Полная версия</span>
            </h3>

            <div class="product__files mt-1 mb-2">
              <a v-for="file in productFiles" :key="file.url" target="_blank" class="product__file" :href="file.url">
                <v-file :name="file.name" :size="file.size" :ext="file.ext" />
              </a>
            </div>
          </template>

          <template v-if="demoFiles.length">
            <h3 class="text-overline">Демо файлы</h3>

            <div class="product__files mt-1">
              <a v-for="file in demoFiles" :key="file.url" target="_blank" class="product__file" :href="file.url">
                <v-file :name="file.name" :size="file.size" :ext="file.ext" />
              </a>
            </div>
          </template>
        </div>
      </main>

      <div class="reviews mt-4 mt-d-6">
        <h2 class="reviews__title text-title pb-1 mb-2">
          Отзывы
          <span class="gray-text ml-1">{{ product.review_count }}</span>
        </h2>

        <ProductReview
          v-if="!product.has_my_review && product.is_owned"
          response-caption="Ответ продавца"
          class="reviews__review mb-3"
          :user="stubReview.user"
          :review="stubReview"
          is-reviewer
          show-form
          @review="createReview"
        />

        <ProductReview
          v-for="review in reviews"
          :key="review.id"
          response-caption="Ответ продавца"
          class="reviews__review mb-3"
          :user="review.user"
          :review="review"
          :is-owner="product.is_creator"
          :is-reviewer="review.isReviewer"
          @answer="sendAnswer"
          @review="sendReview"
        />

        <v-button v-if="canLoadMore" mode="outlined block" class="reviews__load-more mt-2" @click="loadReviews">
          Показать еще
        </v-button>
      </div>

      <v-modal v-model="buyModal" title="Покупка">
        <div v-if="isGiftSuccessSended" class="modal-gift-success flex-column flex-center">
          <v-icon width="320" height="270" class="mt-3 mt-d-0">gift</v-icon>
          <span class="modal-gift-success__title mt-2 mt-d-3">Подарок успешно отправлен!</span>
          <v-button mode="primary xlarge block" class="mt-4 mt-d-6" @click="closeSuccessGift">
            <span class="text-h3">Спасибо!</span>
          </v-button>
        </div>

        <div v-else class="modal-buy">
          <h3 class="modal-buy__title">{{ product.name }}</h3>

          <div class="modal-buy__product mt-2 mt-d-3 mb-2">
            <v-img
              class="modal-buy__product-image product__image mb-1 mr-d-2"
              resize
              :responsive="isMobile"
              :src="product.image"
              :alt="product.name"
              :width="304"
              :height="224"
            />

            <div class="modal-buy__preview-files mt-1 mt-d-0">
              <v-file
                v-for="file in previewFiles"
                :key="file.hashName"
                :name="file.name"
                :size="file.size"
                :ext="file.ext"
              />
            </div>
          </div>

          <div v-if="isOrderToGift" class="gift mt-2 mb-3">
            <div v-if="giftOwner" class="gift__owner-picked">
              <UserSearchBlock :user="giftOwner" />

              <v-button v-if="isMobile" class="mt-3" mode="primary outlined block large" @click="giftOwner = null">
                Изменить получателя
              </v-button>

              <v-link v-else class="text-caption mt-1" mode="primary" @click="giftOwner = null">
                Изменить получателя
              </v-link>
            </div>

            <v-field v-else class="gift__owner-search" mode="bold" label="Кому отправить подарок?">
              <v-input v-model="giftOwnerSearch" placeholder="ФИО получателя" />

              <UserSearchBlock
                v-for="user in usersToGift"
                :key="user.id"
                class="gift__owner mt-1 py-1 px-3 px-d-2"
                :user="user"
                :highlight-text="giftOwnerSearch"
                @click="giftOwner = user"
              />
            </v-field>
          </div>

          <div class="order">
            <template v-if="hasBonusesToBuy">
              <p class="order__balance flex-center text-body-bold">
                Баланс {{ bonusPoints }}
                <v-icon size="16">hand</v-icon>
              </p>

              <v-button mode="warn xlarge block" class="mt-1" @click="buyProduct">
                <span class="flex-center text-h3">
                  Купить {{ product.price }}
                  <v-icon>hand</v-icon>
                </span>
              </v-button>
            </template>

            <template v-else>
              <p class="order__balance flex-center text-body-bold red-text">
                Не хватает {{ (product.price ?? 0) - bonusPoints }}
                <v-icon size="16">hand</v-icon>
              </p>

              <v-button
                mode="primary xlarge block"
                class="mt-1"
                target="_blank"
                :href="`${ctx.urls.purchase_bonus}?next=${currentPage}`"
              >
                Пополнить баланс
              </v-button>
            </template>
          </div>
        </div>
      </v-modal>

      <v-rich-modal v-model="inspiredRejectReasonModal" title="Отклонено методистом">
        <div class="reject-reason-modal">
          <p>
            Товар был отклонен методистом и не получил знак качества. Это не влияет на его доступность на Ярмарке,
            однако методист считает, что материал пока не соответствует критериям, необходимым для получения знака
            качества АС.
          </p>

          <div class="reject-reason-modal__card mt-3"><b>Причина:</b> {{ product.inspired_by_ace_reject_reason }}</div>
        </div>
      </v-rich-modal>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/market/view_product.scss' as *;
</style>
